import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './FormStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const AddProduct = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Basic fields
  const [name, setName] = useState('');
  const [aliasName, setAliasName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [price, setPrice] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [productType, setProductType] = useState('camera');
  const [status, setStatus] = useState('active');
  
  // Images
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  
  // Meta data
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  
  // Advanced
  const [specs, setSpecs] = useState([{ key: '', value: '', unit: '' }]);
  const [keywords, setKeywords] = useState('');
  
  // State
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      setFetchingData(true);
      
      const [categoryRes, typesRes] = await Promise.all([
        axios.get(`${API}/categories`, { timeout: 10000 }),
        axios.get(`${API}/products/types`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        })
      ]);

      console.log('Categories:', categoryRes.data);
      console.log('Product Types:', typesRes.data);

      setCategories(categoryRes.data.data || categoryRes.data || []);
      setProductTypes(typesRes.data.data || typesRes.data || []);
      
    } catch (err) {
      console.error('Fetch metadata error:', err);
      
      let errorMessage = 'Failed to load metadata';
      if (err.response) {
        errorMessage = err.response.data?.message || errorMessage;
      } else if (err.request) {
        errorMessage = 'Cannot connect to server. Check your network.';
      }
      
      alert(errorMessage);
    } finally {
      setFetchingData(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);
    setImages(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      const newPreviews = prev.filter((_, i) => i !== index);
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  const updateSpec = (index, field, value) => {
    const newSpecs = [...specs];
    newSpecs[index][field] = value;
    setSpecs(newSpecs);
  };

  const addSpec = () => {
    setSpecs([...specs, { key: '', value: '', unit: '' }]);
  };

  const removeSpec = (index) => {
    setSpecs(specs.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      alert('Product name is required');
      return;
    }
    
    if (!price || isNaN(parseFloat(price))) {
      alert('Valid price is required');
      return;
    }
    
    if (!categoryId) {
      alert('Please select a category');
      return;
    }
    
    if (!productType) {
      alert('Please select a product type');
      return;
    }
    
    if (images.length === 0) {
      alert('Please add at least one product image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      
      // Basic fields
      formData.append('name', name.trim());
      if (aliasName.trim()) formData.append('alias_name', aliasName.trim());
      if (productCode.trim()) formData.append('product_code', productCode.trim());
      if (description.trim()) formData.append('description', description.trim());
      
      // Pricing
      formData.append('price', parseFloat(price));
      if (costPrice) formData.append('cost_price', parseFloat(costPrice));
      if (mrp) formData.append('mrp', parseFloat(mrp));
      
      // Classification
      formData.append('category_id', categoryId);
      formData.append('product_type', productType);
      formData.append('status', status);
      
      // Images
      images.forEach(image => {
        formData.append('images', image);
      });
      
      // Specs
      const cleanSpecs = specs.filter(s => s.key && s.value);
      if (cleanSpecs.length > 0) {
        formData.append('specs', JSON.stringify(cleanSpecs));
      }
      
      // Keywords
      const keywordArray = keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      if (keywordArray.length > 0) {
        formData.append('keywords', JSON.stringify(keywordArray));
      }

      const response = await axios.post(
        `${API}/products`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000
        }
      );
      
      console.log('Product created:', response.data);
      alert('Product added successfully');
      navigate('/products');
    } catch (err) {
      console.error('Create product error:', err);
      
      let errorMessage = 'Failed to add product';
      
      if (err.response) {
        errorMessage = err.response.data?.message || errorMessage;
        
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Only staff can add products.';
        } else if (err.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
        }
      } else if (err.request) {
        errorMessage = 'Cannot connect to server. Check your network.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="dashboard-main">
          <div className="loading-container">
            <div className="spinner large"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="form-container">
          <div className="form-header">
            <h1>Add Product</h1>
            <p className="form-subtitle">Create a new product</p>
          </div>

          <form onSubmit={handleSubmit} className="modern-form">
            {/* Basic Information */}
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Product Name</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter product name"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Alias Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={aliasName}
                    onChange={(e) => setAliasName(e.target.value)}
                    placeholder="Alternative name"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Product Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                    placeholder="Enter product code"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label required">Category</label>
                  <select
                    className="form-select"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                    disabled={loading}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter product description"
                  rows="4"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="form-section">
              <h2 className="section-title">Pricing</h2>
              
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label className="form-label required">Selling Price</label>
                  <input
                    type="number"
                    required
                    className="form-input"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cost Price</label>
                  <input
                    type="number"
                    className="form-input"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">MRP</label>
                  <input
                    type="number"
                    className="form-input"
                    value={mrp}
                    onChange={(e) => setMrp(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Classification */}
            <div className="form-section">
              <h2 className="section-title">Classification</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Product Type</label>
                  <select
                    className="form-select"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    required
                    disabled={loading}
                  >
                    <option value="camera">Camera</option>
                    <option value="recorder">Recorder</option>
                    <option value="storage">Storage</option>
                    <option value="networking">Networking</option>
                    <option value="power">Power</option>
                    <option value="cabling">Cabling</option>
                    <option value="accessory">Accessory</option>
                    <option value="display">Display</option>
                    <option value="access_control">Access Control</option>
                    <option value="alarm_security">Alarm & Security</option>
                    <option value="tool">Tool</option>
                    <option value="software">Software</option>
                    <option value="service">Service</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={loading}
                  >
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Product Images */}
            <div className="form-section">
              <h2 className="section-title">Product Images *</h2>
              
              <div className="image-upload-container">
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  disabled={loading}
                />
                <label htmlFor="imageUpload" className="btn btn-secondary upload-btn">
                  📁 Choose Images
                </label>
                <p className="form-hint">You can select multiple images</p>
              </div>
              
              {imagePreviews.length > 0 && (
                <div className="image-preview-grid">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeImage(index)}
                        disabled={loading}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="form-section">
              <h2 className="section-title">Specifications</h2>
              
              {specs.map((spec, index) => (
                <div key={index} className="spec-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Key"
                    value={spec.key}
                    onChange={(e) => updateSpec(index, 'key', e.target.value)}
                    disabled={loading}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Value"
                    value={spec.value}
                    onChange={(e) => updateSpec(index, 'value', e.target.value)}
                    disabled={loading}
                  />
                  <input
                    type="text"
                    className="form-input spec-unit"
                    placeholder="Unit"
                    value={spec.unit}
                    onChange={(e) => updateSpec(index, 'unit', e.target.value)}
                    disabled={loading}
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn-icon btn-danger"
                      onClick={() => removeSpec(index)}
                      disabled={loading}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                className="btn btn-link"
                onClick={addSpec}
                disabled={loading}
              >
                + Add Specification
              </button>
            </div>

            {/* Keywords */}
            <div className="form-section">
              <h2 className="section-title">Keywords</h2>
              
              <div className="form-group">
                <label className="form-label">Keywords (comma separated)</label>
                <input
                  type="text"
                  className="form-input"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="e.g., wifi, 4K, outdoor"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                disabled={loading} 
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Creating...
                  </>
                ) : (
                  'Create Product'
                )}
              </button>
              <button 
                type="button" 
                onClick={() => navigate('/products')} 
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;